// Compatibility shim: maps react-router-dom API used in this app to TanStack Router.
import { forwardRef, useCallback, type AnchorHTMLAttributes, type ReactNode } from "react";
import {
  Link as TLink,
  useLocation as useTLocation,
  useParams as useTParams,
  useNavigate as useTNavigate,
} from "@tanstack/react-router";

type LinkProps = {
  to: string;
  replace?: boolean;
  state?: unknown;
  children?: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">;

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(({ to, replace, state, children, ...rest }, ref) => {
  const props: any = { to, replace, state, ...rest };
  return (
    <TLink ref={ref as any} {...props}>
      {children}
    </TLink>
  );
});
Link.displayName = "Link";

type ClassNameFn = (args: { isActive: boolean; isPending: boolean }) => string;

interface NavLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "href" | "children"> {
  to: string;
  end?: boolean;
  replace?: boolean;
  className?: string | ClassNameFn;
  children?: ReactNode | ((args: { isActive: boolean; isPending: boolean }) => ReactNode);
}

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  ({ to, end, className, children, ...rest }, ref) => {
    const location = useTLocation();
    const isActive = end
      ? location.pathname === to
      : location.pathname === to || location.pathname.startsWith(to + "/");
    const isPending = false;
    const cls = typeof className === "function" ? className({ isActive, isPending }) : className;
    const kids = typeof children === "function" ? children({ isActive, isPending }) : children;
    const props: any = { to, className: cls, ...rest };
    return (
      <TLink ref={ref as any} {...props}>
        {kids}
      </TLink>
    );
  },
);
NavLink.displayName = "NavLink";

export function useLocation() {
  const loc: any = useTLocation();
  return {
    pathname: loc.pathname as string,
    search: (loc.searchStr ?? "") as string,
    hash: (loc.hash ?? "") as string,
    state: loc.state,
    key: "default",
  };
}

export function useParams<T extends Record<string, string> = Record<string, string>>() {
  return useTParams({ strict: false } as any) as T;
}

export function useNavigate() {
  const nav = useTNavigate();
  return useCallback((to: string | number, opts?: { replace?: boolean; state?: unknown }) => {
    if (typeof to === "number") {
      if (typeof window !== "undefined") window.history.go(to);
      return;
    }
    nav({ to, replace: opts?.replace, state: opts?.state } as any);
  }, [nav]);
}

export function Navigate({ to, replace }: { to: string; replace?: boolean }) {
  const nav = useTNavigate();
  if (typeof window !== "undefined") {
    nav({ to, replace } as any);
  }
  return null;
}

// No-ops — file-based routing handles these.
export const BrowserRouter = ({ children }: { children?: ReactNode }) => <>{children}</>;
export const Routes = ({ children }: { children?: ReactNode }) => <>{children}</>;
export const Route = (_: unknown) => null;
export { Outlet } from "@tanstack/react-router";
